import { ListIcon as BurgerMenuIcon } from "@phosphor-icons/react";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { useT } from "@transifex/react";
import { HeaderLink, TeacherResponse, UserInfo } from "p6m-p6u";
import { type MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import { useLogger } from "../../hooks/useLogger";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import HeaderBurgerMenuLoginLogoutButton from "./HeaderBurgerLoginLogoutButton";
import HeaderBurgerMenuAccount from "./HeaderBurgerMenuAccount";

interface HeaderBurgerMenuProps {
    mainPageLinks: HeaderLink[];
    mainPageUrl: string;
    userInfo?: UserInfo;
    teacherInfo?: TeacherResponse;
    avatarUrl: string;
    currentPath: string;
    accountTranslations: {
        myAccount: string;
        myParentAccount: string;
        myLearningAccount: string;
    };
}

const _handleClickOutside = (e: MouseEvent, callback: () => void) => {
    if (!(e.target instanceof Element)) {
        return;
    }

    // don't close for marked elements
    if (e.target.closest(".no-close-burgerdropdown")) {
        return;
    }

    callback();
};

const HeaderBurgerMenu = (props: HeaderBurgerMenuProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();
    const { logEvent } = useLogger();

    const t = useT();

    const t_sr_toggleDropdown = t("toggle navigation menu", {
        _tags: "TeacherArea, Account, ScreenReaderOnly",
    });

    const t_sr_mainPage = t("Go to phase6 main page", {
        _tags: "TeacherArea,Header,ScreenReaderOnly",
    });

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const baseClasses =
        "rounded-xsmall-container border border-stone-400 bg-white flex-col divide-y divide-stone-400 shadow-sm p-1";

    const positioningClasses = "absolute top-[calc(var(--size-header-m)+var(--spacing-xxs))] right-xxs z-100";

    const sizeClasses =
        "min-w-[312px] max-w-[calc(100vw-2*var(--spacing-xs))] max-h-[calc(100vh-var(--size-header-m)-2*var(--spacing-xs))] overflow-y-scroll";

    // Only fall back to the login name when the teacher has no name at all (e.g. SSO sign-up),
    // so a partial name is never concatenated with the login handle.
    const teacherFirstName = props.teacherInfo?.firstname || "";
    const teacherLastName = props.teacherInfo?.lastname || "";
    const hasTeacherName = !!teacherFirstName || !!teacherLastName;
    const accountLastName = hasTeacherName ? teacherLastName : props.userInfo?.userName || "";

    const toggleDropdown = (e: ReactMouseEvent) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
    };

    const handleLinkClick = () => {
        setIsOpen(false);
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            _handleClickOutside(e, () => {
                setIsOpen(false);
            });
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);

    // Handle escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setIsOpen(false);
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            const screenSize = window.innerWidth;
            const lgBreakPoint = 1024;
            if (isOpen && screenSize >= lgBreakPoint) {
                setIsOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isOpen]);

    return (
        <div className={`gap-s flex items-center ${getStyleBasedOnBreakpoint(Breakpoint.LG, "hidden", "flex")}`}>
            <a
                className="text-stone-1000 hover:text-forest-green-800 text-header-icon-lg! flex items-center transition-colors duration-300 ease-in-out"
                aria-label={t_sr_mainPage}
                href={props.mainPageUrl}
                onClick={() => logEvent("Menu Main Page Clicked", { source: "mobile" })}
            >
                <PhaseSixIcon name="house" />
            </a>

            <div className="relative">
                <button
                    ref={buttonRef}
                    className="text-stone-1000 py-m pr-m flex cursor-pointer"
                    id="burger-menu-button"
                    aria-expanded={isOpen}
                    aria-controls="burger-dropdown"
                    aria-label={t_sr_toggleDropdown}
                    onClick={toggleDropdown}
                >
                    <PhosphorIcon
                        phosphorIcon={BurgerMenuIcon}
                        fontSize="text-[30px]"
                    />
                </button>

                <div
                    ref={dropdownRef}
                    className={`${positioningClasses} ${baseClasses} ${sizeClasses} ${isOpen ? "flex" : "hidden"}`}
                    id="burger-dropdown"
                    role="menu"
                >
                    {!props.userInfo?.isAnonymousUser && (
                        <HeaderBurgerMenuAccount
                            avatarUrl={props.avatarUrl}
                            currentPath={props.currentPath}
                            firstName={teacherFirstName}
                            lastName={accountLastName}
                            role={props.userInfo?.visitorOrUserRole ?? ""}
                            accountTranslations={props.accountTranslations}
                        />
                    )}

                    {props.mainPageLinks?.map((element) => (
                        <a
                            key={element.text}
                            href={element.link}
                            id={`${element.text}-headerlink`}
                            className="text-stone-1000 text-header-l py-s px-m gap-xs dropdown-link group mx-1 flex w-full flex-row items-center justify-start rounded-sm"
                            role="menuitem"
                            onClick={handleLinkClick}
                        >
                            <p
                                className={`text-stone-1000 header-nav-link ${
                                    element.link.includes(props.currentPath) ? "header-nav-link-active" : ""
                                }`}
                                data-link-path={element.link}
                            >
                                {element.text}
                            </p>
                        </a>
                    ))}

                    <HeaderBurgerMenuLoginLogoutButton
                        isUser={!props.userInfo?.isAnonymousUser}
                        onLogout={handleLinkClick}
                    />
                </div>
            </div>
        </div>
    );
};

export default HeaderBurgerMenu;
